import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import useAutoRunOnFilterChange from '@hooks/useAutoRunOnFilterChange'
import useLatestRun from '@hooks/useLatestRun'
import useReadState from '@hooks/useReadState'
import dayjs from 'dayjs'
import accountingReportService, { accountingEndpoint, accountingAttachFormats } from '@api/v1/accountingReportService'
import reportJobService from '@api/v1/reportJobService'
import SpecializedReportShell from '@components/reports/SpecializedReportShell'
import SpecializedFilterBar from '@components/reports/SpecializedFilterBar'
import { dateRangeField, choiceField, boolField, listField, accountsField, ALL } from '@components/reports/specializedFilterFields'
import DatePresetSelect from '@components/reports/DatePresetSelect'
import ReportGroupedGrid from '@components/reports/ReportGroupedGrid'
import { stableAliasKeys } from '@components/reports/aliasedGridColumns'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import { describeFilters, dateRangePart, basisPart } from '@components/reports/reportFilterSummary'
import { generalLedgerSavedCriteria, savedGlAccountIds } from '@components/reports/savedRunCriteria'
import { useReportExportView } from '@components/reports/reportExportViewContext'
import { accountOptionsQueryKey, fetchAccountOptions } from '@components/reports/accountPickerOptions'
import { useAccountsIndex, accountLinkHtml } from './accountLinks'
import {
  ACCOUNT_TYPE_OPTIONS,
  toGlWire,
  selectedAccountIds,
  isSpecificSelection,
  staleAccountIds,
  allStaleMessage,
  someStaleMessage,
  CHART_UNREADABLE_MESSAGE,
  accountScopePart,
} from './glAccountScope'
import {
  readSubAccountColumns,
  realignedNotice,
  refusedWarning,
  refusedAccounts,
  uncheckedNotice,
  UNVERIFIED_SECTION_LABEL,
  glXlsxRefusal,
  glClientExportRefusal,
  GL_XLSX_SERVER_REFUSAL_MESSAGE,
} from './glSubAccountColumns'

/**
 * General Ledger — wraps the CSV export and parses server-side into
 * structured JSON ({ headers, rows }). Account section rows (detected
 * heuristically: only col0 populated) become ReportGroupedGrid groups —
 * section row → groupHeaderContent, following rows → dataRows.
 *
 * GUARDRAIL: the Balance column is a RUNNING balance, so re-sorting rows
 * across sections would render it meaningless. The grouped grid only sorts
 * WITHIN a group (acceptable); do NOT flatten sections into one sortable
 * list here. Within a group only the TRANSACTIONS sort: each account's
 * Beginning Balance and Totals are pinned to its ends (CC-54054, below).
 */

const RIGHT_RE = /(amount|balance|debit|credit|total)/i

/*
 * CC-53891 (spec 128 §B2) — CB's GL CSV heads its first column "Type", and it
 * holds the TRANSACTION type (Payment, Deposit, Check…); the account name only
 * appears as the group heading. Robin typed "Bank" into it expecting Bank
 * ACCOUNTS and got an empty grid (0 of 744 rows match, measured). The label now
 * says what the column is. Display only: the column's IDENTITY (`columnId`,
 * `aliasKey`) is still CB's header text 'Type', so saved layouts, sorts, column
 * filters and firm aliases keyed on it are untouched. Filtering by account type
 * is the server-side Account Types control (Bank and Trust included) — no
 * client-side heading match, which the measurement showed would widen existing
 * filters and match "Bank Service Charges", an expense account.
 */
// A Map, not an object literal: a CSV header is data, and `{}['constructor']`
// would hand back a function as a column label.
const DISPLAY_LABEL = new Map([['Type', 'Transaction Type']])

// displayValue / groupHeaderContent are injected as HTML by the universal grid.
const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

// Parse "$1,234.56" / "(500.00)" into a number for dataValue (undefined if not numeric).
const parseAmount = (v) => {
  if (v == null || String(v).trim() === '') return undefined
  const str = String(v)
  const n = Number(str.replace(/[^0-9.-]/g, ''))
  if (!Number.isFinite(n)) return undefined
  return str.includes('(') ? -Math.abs(n) : n
}

// CC-49594: publish the grid's export view to the surrounding shell so it can
// offer the client-built PDF. The context provider lives inside
// SpecializedReportShell, so this wrapper must render as a child of the shell.
//
// CC-53921 (spec 157): the published view also carries `clientExportRefusal`
// (format → reason | null), which ReportExport honours before it builds a file from
// the view — a client-built CSV/Excel has no account headings, so it cannot carry a
// refused section's marker (glSubAccountColumns.js, glClientExportRefusal). Read
// through a ref, so the getter the shell holds always answers for the rows on screen.
// It is told whether the view holds an explicit "Show $" choice, because that choice
// alone makes every CSV/Excel client-built (CC-50004) and the advice must say so.
const PublishedGrid = ({ report, storageKey, pageStateRef, initialPageState, clientExportRefusal }) => {
  const exportCtx = useReportExportView()
  const exportViewRef = useRef(null)
  const refusalRef = useRef(clientExportRefusal)
  useEffect(() => { refusalRef.current = clientExportRefusal }, [clientExportRefusal])
  useEffect(() => {
    if (!exportCtx) return
    exportCtx.ref.current = () => {
      const view = exportViewRef.current?.()
      const refuse = refusalRef.current
      if (!view || typeof refuse !== 'function') return view
      const currencyChoice = view.currencyOverride != null
      return { ...view, clientExportRefusal: (format) => refuse(format, { currencyChoice }) }
    }
    exportCtx.onPublish?.()
  }, [exportCtx])
  // CC-49732: forward the saved-view page-state plumbing (see BalanceSheetReport).
  return (
    <ReportGroupedGrid
      report={report}
      storageKey={storageKey}
      exportViewRef={exportViewRef}
      pageStateRef={pageStateRef}
      initialPageState={initialPageState}
    />
  )
}

/*
 * CC-50326 — account TYPE, not a frozen list of account ids: values are CB's
 * `AccountCategories` enum ints, resolved by cb-api to their current member
 * accounts on every run. CC-53890 (spec 128) adds the SPECIFIC-accounts picker
 * beside it, plus Bank and Trust as types. The options, the wire rules and the
 * refusals live in ./glAccountScope.js.
 */
const EMPTY_DATA = { headers: [], rows: [] }

const makeDefaults = () => ({
  startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
  endDate: dayjs().format('YYYY-MM-DD'),
  isCashBasis: true,
  showZeroBal: false,
  showCurrSymbol: true,
  // Pipe-joined category ints. A TOKEN, deliberately — never resolved ids, or it
  // freezes the moment it is saved (which is the defect CC-50326 reports).
  accountCategories: '',
  // CC-53890: ALL (every account) | '' (none selected — refuses) | 'id|id'.
  // A page-state key, not a wire key: toGlWire turns it into CB's
  // `filteredAccounts` only when it names accounts.
  accountSelection: ALL,
})

/**
 * The run's wire, or a refusal. Every path that leaves the page — the run, the
 * export, a queued or scheduled job — goes through this, so none of them can
 * send what another refused. Throws the refusal for the paths that report
 * errors by exception (the queue control toasts it; the export alerts it).
 */
const wireOrThrow = (f, extraRefusal) => {
  const { wire, refusal } = toGlWire(f)
  const why = refusal || extraRefusal
  if (why) throw new Error(why)
  return wire
}

// Basis is a mutually exclusive choice → RADIO. Cash-vs-Accrual must never be
// multi-selectable. omitAll: a GL needs a bounded window (the page validates the
// range and refuses to run without one).
const FIELDS = [
  dateRangeField({ key: 'dates', label: 'Date Range', startKey: 'startDate', endKey: 'endDate', omitAll: true }),
  choiceField({
    key: 'basis', label: 'Basis', valueKey: 'isCashBasis',
    options: [{ value: 'cash', label: 'Cash' }, { value: 'accrual', label: 'Accrual' }],
    encode: (v) => (v ? 'cash' : 'accrual'),
    decode: (s) => s === 'cash',
  }),
  boolField({ key: 'showZeroBal', label: 'Show zero balances', valueKey: 'showZeroBal' }),
  // CC-50326. Multi-select: "all expenses AND all equity" is a real ask. Since
  // CC-53890 the options overlap (Bank and Trust are Assets), which is harmless:
  // cb-api resolves them into a SET of accounts, so nothing counts twice.
  listField({
    key: 'accountTypes',
    label: 'Account Types',
    valueKey: 'accountCategories',
    options: ACCOUNT_TYPE_OPTIONS,
    // No type chosen = every account, so say "All", not "None selected" — on
    // this page "None selected" is reserved for the one control where it refuses.
    emptyIsAll: true,
    // A type is a scope of its own: choosing one returns Accounts to All, so the
    // page never holds a type AND a list. Emptying the types (or its "All" row)
    // chooses nothing and leaves a specific-account pick alone.
    alsoSet: { accountSelection: ALL },
  }),
  // CC-53890 (spec 128): specific accounts, by explicit id. Picking any clears
  // Account Types (see `clears`) — the wire carries one scope, never both.
  accountsField({
    key: 'accounts',
    label: 'Accounts',
    valueKey: 'accountSelection',
    clears: { accountCategories: '' },
    help: 'Ticking a parent account also ticks its sub-accounts. Choosing specific accounts replaces any Account Types choice.',
  }),
]

// Module-level so useLatestRun's `run` keeps one identity (spec 131).
const runErrorMessage = (err) => err.message || 'Report failed'

const GeneralLedgerReport = ({ reportId, name = 'General Ledger', summary, jobId }) => {
  const [filters, setFilters] = useState(makeDefaults)

  // CC-53890: the chart of accounts, read only while specific accounts are
  // selected — to NAME them in the export masthead and to notice ids that are no
  // longer in the chart. Same query key and builder as the picker's own list, so
  // opening the picker costs no second request and the two can never label an
  // account differently.
  const specific = isSpecificSelection(filters.accountSelection)
  // Spec 185 (CC-53435): a reopened job's own stored params — read ONLY to state the
  // saved run's criteria on the documents (`savedCriteria` below). Never seeded into
  // `filters`: that would fire useAutoRunOnFilterChange and replace the saved figures
  // with a fresh run. Set by the reopen's commit, cleared by any other.
  const [savedParams, setSavedParams] = useState(null)
  // A reopened job that ran SPECIFIC accounts is named the way the live line names
  // them, so the chart is read for it too. `chartPending` below stays keyed on the
  // live selection alone — a reopen never holds or starts a run.
  const savedSpecific = savedGlAccountIds(savedParams).length > 0
  const chartQuery = useQuery({
    queryKey: accountOptionsQueryKey(),
    queryFn: () => fetchAccountOptions(),
    enabled: specific || savedSpecific,
    staleTime: 60_000,
  })
  const chartState = useReadState('gl-chart-of-accounts', chartQuery)
  const chartOptions = chartState === 'ready' ? chartQuery.data : null
  // The run WAITS for the chart when specific accounts are selected, because two
  // things must be known before a request goes out (and before the document's
  // criteria line is captured at run start — spec 131, `useLatestRun`): whether every id is gone, and
  // what each account is called. Picking in the picker leaves the chart cached,
  // so this only waits when a saved view brings ids in cold. A chart that cannot
  // be read does not block the run — the notice below says it was not checked.
  const chartPending = specific && (chartState === 'loading' || chartState === 'idle')
  const labelsById = useMemo(
    () => (chartOptions ? new Map(chartOptions.map((o) => [o.key, o.value])) : null),
    [chartOptions]
  )
  const selectedIds = selectedAccountIds(filters.accountSelection)
  const staleIds = useMemo(
    () => (chartOptions ? staleAccountIds(filters.accountSelection, chartOptions) : []),
    [chartOptions, filters.accountSelection]
  )
  // Every selected id gone: CB would answer "Grand Total $0.00", which renders
  // as a one-row General Ledger rather than an empty state (measured) — a
  // confident-looking report of nothing. Refuse instead. SOME gone: run, and say
  // so (never strip them — an emptied list is the all-accounts hazard).
  const allStale = selectedIds.length > 0 && staleIds.length === selectedIds.length
  const accountsRefusal = toGlWire(filters).refusal || (allStale ? allStaleMessage(selectedIds.length) : null)
  const accountsNotice = !accountsRefusal && staleIds.length > 0
    ? someStaleMessage(staleIds.length, selectedIds.length)
    : (specific && (chartState === 'failed' || chartState === 'blocked') ? CHART_UNREADABLE_MESSAGE : null)

  // CC-50029: state the date window + basis in the exported header block.
  const filtersSummary = useMemo(
    () => describeFilters(
      dateRangePart(filters.startDate, filters.endDate),
      basisPart(filters.isCashBasis),
      filters.showZeroBal ? 'Including zero balances' : null,
      // CC-50326 / CC-53890: a financial export has to say what it covered —
      // the account TYPES, or each SPECIFIC account by name. Neither CB's own
      // export header nor cb-api's stamp names them.
      accountScopePart(filters, labelsById),
    ),
    [filters, labelsById]
  )
  // CC-53890: each run takes a ticket; only the newest may write the grid. A
  // refusal takes one too, so a run still in flight when the selection became
  // unrunnable cannot land its figures under the refusal.
  //
  // Spec 131: that ticket (`runSeqRef` until now) is `useLatestRun`, the same one
  // all 20 specialized pages use. It also gates the spinner, the error and — new
  // here — the committed run's CRITERIA, which this page's rows were guarded
  // against but its documents were not: the shell's old running-edge snapshot
  // named the FIRST run of a chain over the LAST run's rows (RACE-2, GL 363).
  const {
    running, error, ran, runKey, run: startRun, invalidate: invalidateRun,
  } = useLatestRun({ initialRunning: !!jobId, errorMessage: runErrorMessage })
  const [data, setData] = useState(EMPTY_DATA)
  const [savedAsOf, setSavedAsOf] = useState(null)
  // CC-53921: whether the rows on screen were reopened from the tray (a saved
  // result) or came from a run just now — `savedAsOf` alone is null when the job's
  // status carried no time. Set in the same commit as the rows.
  const [fromSavedJob, setFromSavedJob] = useState(false)
  // Consume-once: the first run reopens the tray's saved job; any later run
  // (filter change / Run button) then executes fresh.
  const jobRef = useRef(jobId || null)
  // CC-49839: bump to remount the grid after a saved view writes layout prefs.
  const [gridKey, setGridKey] = useState(0)
  // CC-49732: grid publishes quick-filters + toggles here; appliedGridStateRef
  // carries a restored view's state into the remounted grid (mount-only read).
  const pageStateRef = useRef(null)
  const appliedGridStateRef = useRef(null)
  const accountsIndex = useAccountsIndex() // CC-49837

  // null while refused — there is nothing that may be sent.
  const params = useMemo(() => toGlWire(filters).wire, [filters])

  // Spec 185 (CC-53435): a reopened job's criteria line, from ITS params, worded by the
  // same parts as the live `filtersSummary` above (accountScopePart included). While the
  // chart that names a saved account list is still loading, nothing is claimed yet
  // (null → "not recorded"); once it settles the accounts are named, or — if the chart
  // could not be read — counted, exactly as the live line words that state.
  const savedChartSettled = !savedSpecific || (chartState !== 'loading' && chartState !== 'idle')
  const savedCriteria = useMemo(
    () => (savedAsOf != null && savedChartSettled ? generalLedgerSavedCriteria(savedParams, labelsById) : null),
    [savedAsOf, savedChartSettled, savedParams, labelsById]
  )

  // CC-49991: an inverted range (end before start) is a user error, not a server
  // error — but CB answers it with a 500 ("Start date cannot be greater than the
  // end date when determining an accounting period"), which cb-api surfaces as an
  // opaque "Export fetch failed" and the user sees "Request failed with status
  // code 500". Since the range auto-runs on change, editing the end date to an
  // earlier month reliably lands in that state (QA's repro: 07/28 → 06/28 with a
  // July start). Catch it here and say something useful instead of firing a
  // request we know cannot succeed.
  const rangeError = useMemo(() => {
    const { startDate, endDate } = filters
    if (!startDate || !endDate) return null
    return dayjs(endDate).isBefore(dayjs(startDate), 'day')
      ? 'End date is before the start date — adjust the range to run this report.'
      : null
  }, [filters])

  const runReport = useCallback(() => {
    // Never send a range the server must reject. Spec 131: and retire a run still
    // in flight, so it cannot land under the rangeError message. No error: the
    // message renders beside the bar, not in the red pane.
    if (rangeError) { invalidateRun(); return undefined }
    const jid = jobRef.current
    // Re-fired by the auto-run key below the moment the chart settles. Spec 131:
    // the wait now RETIRES a run still in flight (it used to take no ticket and
    // let that run land while the page said it was checking the selection).
    if (chartPending && !jid) { invalidateRun(); return undefined }
    if (accountsRefusal && !jid) {
      // CC-53890: nothing is sent, and the previous selection's figures do not
      // stay on screen under a filter that now says something else. The reason
      // renders beside the bar (role="alert"). `clearRan`: with the rows gone the
      // documents must stop naming the run that produced them.
      invalidateRun({ error: null, clearRan: true })
      setData(EMPTY_DATA); setSavedAsOf(null); setFromSavedJob(false); setSavedParams(null)
      return undefined
    }
    return startRun(async () => {
      jobRef.current = null // consumed — subsequent runs are fresh
      if (jid) {
        // Tray "View report" → reopen the exact saved snapshot + show its run time.
        const [st, r] = await Promise.all([reportJobService.status(jid), reportJobService.result(jid)])
        return () => { setSavedAsOf(st?.createdAt || null); setFromSavedJob(true); setSavedParams(st?.savedParams ?? null); setData(r?.data || EMPTY_DATA) }
      }
      const result = await accountingReportService.generalLedger.run(params, { reportId })
      // Spec 131 decision 3: the saved-result banner clears at the COMMIT.
      return () => { setSavedAsOf(null); setFromSavedJob(false); setSavedParams(null); setData(result || EMPTY_DATA) }
    }, { criteria: { filtersSummary } })
  }, [params, rangeError, accountsRefusal, chartPending, reportId, startRun, invalidateRun, filtersSummary])

  // CC-49835: first load + re-run whenever a filter changes (debounced so the
  // date inputs don't fire a fetch per keystroke). The page previously had NO
  // run control at all — data was fetched once on mount and date changes were
  // silently ignored. CC-53890: the refusal and the chart wait are part of the
  // key, so the run that was held for the chart fires when it settles, and a
  // refusal that only the chart can reveal (every saved id gone) takes effect
  // without the filters moving.
  useAutoRunOnFilterChange(runReport, { params, accountsRefusal, chartPending })

  // CC-49839: applying a saved view restores the run filters (the auto-run
  // effect re-fetches) and remounts the grid so it re-reads the saved layout.
  //
  // CC-53890: the account scope is RESET before the view is laid over, because
  // this is a merge — a view saved before a key existed would otherwise inherit
  // whatever that key holds on screen now, and a view saved as "all accounts"
  // would silently run the two accounts somebody just picked. A view that does
  // carry the keys overrides the reset.
  const applyView = useCallback((view) => {
    if (view?.filters && Object.keys(view.filters).length) {
      setFilters((f) => ({ ...f, accountCategories: '', accountSelection: ALL, ...view.filters }))
    }
    // CC-49732: v2 views carry the grid's page state; v1 views have none.
    appliedGridStateRef.current = view?.v >= 2 ? (view.pageState?.grid || null) : null
    setGridKey((k) => k + 1)
  }, [])

  // CC-50013 G2 / CC-50148: shared metadata-driven bar (see FIELDS).
  //
  // CC-49991: the inverted-date-range message used to sit inside the bespoke bar's
  // grid. It now renders directly BELOW the bar — still adjacent to the controls
  // that caused it, still `role="alert"`, and the Run control stays disabled while
  // it is showing.
  const filterBar = (
    <>
        <SpecializedFilterBar
        fields={FIELDS}
        filters={filters}
        onFiltersChange={setFilters}
        defaults={makeDefaults()}
        onRun={runReport}
        isLoading={running}
        disabled={!!rangeError || !!accountsRefusal || chartPending}
        queue={{
          reportId,
          name,
          // Mirrors accountingReportService.generalLedger.run: an ACCOUNTING job
          // (GET replay), key 'general-ledger' — which stays per-user server-side
          // because of bank-account security.
          kind: 'accounting',
          accountingKey: 'general-ledger',
          // CC-53890: a refused selection THROWS here, and the queue control
          // toasts it — a queued or scheduled job must not be the way round a
          // refusal the Run button honours.
          endpoint: (f) => accountingEndpoint('/reports/accounting/general-ledger/data', wireOrThrow(f, allStale ? accountsRefusal : null)),
          filters: (f) => wireOrThrow(f, allStale ? accountsRefusal : null),
          // Spec 194: the emailed copy can carry this page's own export.
          attachFormats: accountingAttachFormats('/reports/accounting/general-ledger/data'),
        }}
        actions={(
          /* CC-49732: getPageState is what makes the saved view v2 and captures
             the grid's quick-filters/toggles so they can be restored. */
          <ReportViewsMenu
            storageKey="report:general-ledger"
            currentFilters={filters}
            onApply={applyView}
            getPageState={() => ({ grid: pageStateRef.current?.() || null })}
          />
        )}
      />
      {/* CC-49991: inline, adjacent to the controls that caused it. */}
      {rangeError && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 mb-4 text-sm text-red-800"
        >
          <span aria-hidden="true" className="mt-px font-semibold">!</span>
          <span>{rangeError}</span>
        </div>
      )}
      {/* CC-53890: same treatment for an account selection that cannot run. */}
      {accountsRefusal && (
        <div
          role="alert"
          data-testid="gl-accounts-refusal"
          className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 mb-4 text-sm text-red-800"
        >
          <span aria-hidden="true" className="mt-px font-semibold">!</span>
          <span>{accountsRefusal}</span>
        </div>
      )}
      {chartPending && (
        <div role="status" data-testid="gl-accounts-checking" className="mb-4 text-sm text-gray-600">
          Checking the selected accounts against the chart of accounts…
        </div>
      )}
      {/* CC-53890: runs, but the reader must know part of the selection is gone
          (or could not be checked). A status, not an alert: nothing was refused.
          amber-900 on amber-50 is 8.77:1 (Tailwind v4 palette, computed). */}
      {accountsNotice && (
        <div
          role="status"
          data-testid="gl-accounts-notice"
          className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 mb-4 text-sm text-amber-900"
        >
          <span aria-hidden="true" className="mt-px font-semibold">!</span>
          <span>{accountsNotice}</span>
        </div>
      )}
    </>
  )

  // CC-53921 (spec 157): cb-api's account of CB's sub-account rows — moved under
  // their headings, or left as CB wrote them and named. `undefined` = never
  // checked (a result saved before the check existed, or a fresh answer from a
  // worker without it); see glSubAccountColumns.js.
  const subAccountColumns = useMemo(() => readSubAccountColumns(data), [data])
  const hasRows = (data.rows?.length || 0) > 0
  const unverifiedAccounts = useMemo(() => refusedAccounts(subAccountColumns), [subAccountColumns])
  const subAccountWarning = refusedWarning(subAccountColumns)
  const subAccountNotice = realignedNotice(subAccountColumns)
  const subAccountUnchecked = subAccountColumns === undefined && hasRows
  // What a file built from the grid's view may carry (ReportExport asks per format).
  const clientExportRefusal = useCallback(
    (format, { currencyChoice = false } = {}) => glClientExportRefusal(subAccountColumns, { hasRows, currencyChoice }, format),
    [subAccountColumns, hasRows]
  )

  // CB CSV section heuristic (unchanged): a row is an account section header
  // when only its first column is populated.
  const gridReport = useMemo(() => {
    const headers = data.headers || []
    const rows = data.rows || []
    const isSection = (row) => {
      const vals = headers.map((h) => String(row[h] ?? '').trim())
      if (!vals[0]) return false
      return vals.slice(1).every((v) => v === '')
    }
    // CC-50291: `columnId` is already the CB header text, so it is a stable
    // identity here — but route it through stableAliasKeys anyway so a repeated
    // or blank header cannot collapse two columns onto one firm rename.
    const aliasKeys = stableAliasKeys(headers)
    const columns = headers.map((h, i) => {
      const label = DISPLAY_LABEL.get(h)
      return {
        columnId: h,
        aliasKey: aliasKeys[i],
        label: label || h,
        // CC-53891: CB's own name stays visible to the column-alias editor and
        // its drift check, which read `rawLabel ?? label` as "what Centerbase
        // calls this column" — a firm alias keyed on 'Type' keeps working.
        ...(label ? { rawLabel: h } : {}),
        visible: true,
        columnAlignment: RIGHT_RE.test(h) ? 'RIGHT' : 'LEFT',
      }
    })
    const toLeaf = (row, pin) => ({
      isGroup: false,
      ...(pin ? { pin } : {}),
      dataCells: headers.map((h) => {
        const raw = row[h] ?? ''
        const cell = { columnId: h, displayValue: esc(raw) }
        if (RIGHT_RE.test(h)) {
          // dataValue only on amount-like columns so within-group sort is value-based.
          const n = parseAmount(raw)
          if (n != null) cell.dataValue = n
        }
        return cell
      }),
    })
    // CC-54054 (spec 141): each account section opens with its Beginning Balance
    // and closes with its Totals (CB writes "Totals" or "Total {account}", and the
    // last section also carries the report's Grand Total). Those are balances, not
    // transactions, and they are the only rows CB sends without a Date (measured
    // over five runs, 4,486 rows, 2026-10-08). So the undated rows BEFORE an
    // account's first transaction pin first and those AFTER its last pin last, and
    // a column sort reorders only the transactions between them — the way CB's own
    // General Ledger lays an account out. (An account with no transactions has
    // every row pinned to one end, so it keeps CB's order.) No Date column, no
    // pins: the grid sorts as it always did.
    const dateKey = headers.find((h) => String(h).trim().toLowerCase() === 'date')
    const pinsFor = (sectionRows) => {
      if (dateKey == null) return []
      const dated = sectionRows.map((r) => String(r[dateKey] ?? '').trim() !== '')
      const first = dated.indexOf(true)
      const last = dated.lastIndexOf(true)
      return sectionRows.map((_, i) => (i < first ? 'first' : i > last ? 'last' : undefined))
    }
    const reportData = []
    const sections = []
    let current = null
    for (const row of rows) {
      if (isSection(row)) {
        const label = String(row[headers[0]] ?? '').trim()
        // CC-53921: a section cb-api could not verify carries its warning on its
        // own heading — on screen, in print, and in the client-built PDF, which all
        // render headings. It does NOT reach a client-built CSV or Excel (leaf rows
        // only, no headings), so those are refused for such a run instead
        // (glClientExportRefusal). amber-900 on amber-50 is 8.77:1 (Tailwind v4
        // palette, computed).
        const unverified = unverifiedAccounts.has(label)
          ? ` <span data-gl-columns-unverified class="ml-2 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-900">${esc(UNVERIFIED_SECTION_LABEL)}</span>`
          : ''
        current = {
          group: {
            isGroup: true,
            // CC-49837: GL sections ARE accounts — link the header to the register.
            groupHeaderContent: accountLinkHtml(accountsIndex, label, esc(label)) + unverified,
            dataRows: [],
          },
          rows: [],
        }
        sections.push(current)
        reportData.push(current.group)
      } else if (current) {
        current.rows.push(row)
      } else {
        // Preamble rows before the first section render as top-level leaves.
        reportData.push(toLeaf(row))
      }
    }
    for (const section of sections) {
      const pins = pinsFor(section.rows)
      section.group.dataRows = section.rows.map((row, i) => toLeaf(row, pins[i]))
    }
    return { columns, reportData, reportFooterCells: [] }
  }, [data, accountsIndex, unverifiedAccounts])

  return (
    <SpecializedReportShell
      name={name} summary={summary} running={running} error={error}
      ranCriteria={ran?.criteria ?? null} runKey={runKey}
      filterBar={filterBar} summaryData={data}
      filtersSummary={filtersSummary} savedAsOf={savedAsOf}
      savedRunFiltersSummary={savedAsOf != null ? savedCriteria : undefined}
      // CC-53890: the server export refuses exactly what the Run refuses — it is
      // the same request, and an export of "None selected" would otherwise be a
      // file of EVERY account (the shell alerts the thrown reason).
      download={(format, getToken) => {
        const wire = wireOrThrow(filters, allStale ? accountsRefusal : null)
        // CC-53921: CB's workbook carries sub-account rows one column short and
        // cb-api cannot correct a binary it did not write, so it refuses (409).
        // Say why before asking — including for rows nobody checked, which must
        // not be waved through to whichever worker answers — and say it again if
        // the server is the one that noticed (a run whose rows are not on screen).
        const refusal = format === 'xlsx' ? glXlsxRefusal(subAccountColumns, { hasRows }) : null
        if (refusal) throw new Error(refusal)
        const download = accountingReportService.generalLedger.download(wire, format, getToken)
        return format === 'xlsx'
          ? download.catch((err) => {
            if (/\b409\b/.test(String(err?.message))) throw new Error(GL_XLSX_SERVER_REFUSAL_MESSAGE)
            throw err
          })
          : download
      }}
    >
      {/* CC-53921 (spec 157): what happened to CB's sub-account rows on this run.
          An alert when a section could not be verified — its figures may sit in
          the wrong columns — and a status line when rows were moved (Ref Number
          is then blank: CB never sends it). amber-900 on amber-50 is 8.77:1;
          gray-600 on white is 7.56:1. */}
      {subAccountWarning && (
        <div
          role="alert"
          data-testid="gl-subaccount-warning"
          className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 mb-3 text-sm text-amber-900"
        >
          <span aria-hidden="true" className="mt-px font-semibold">!</span>
          <span>{subAccountWarning}</span>
        </div>
      )}
      {subAccountUnchecked && (
        <div
          role="status"
          data-testid="gl-subaccount-unchecked"
          className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 mb-3 text-sm text-amber-900"
        >
          <span aria-hidden="true" className="mt-px font-semibold">!</span>
          <span>{uncheckedNotice({ saved: fromSavedJob })}</span>
        </div>
      )}
      {subAccountNotice && (
        <p role="status" data-testid="gl-subaccount-notice" className="mb-3 text-sm text-gray-600">
          {subAccountNotice}
        </p>
      )}
      <PublishedGrid
        key={gridKey}
        report={gridReport}
        storageKey="report:general-ledger"
        pageStateRef={pageStateRef}
        clientExportRefusal={clientExportRefusal}
        // Pre-existing (CC-49732): a MOUNT-only seed read behind a gridKey bump.
        // Surfaced, not introduced, by spec 131 — the React Compiler lint skipped
        // this whole component while its runReport held a try/finally, which now
        // lives in useLatestRun.
        // eslint-disable-next-line react-hooks/refs
        initialPageState={appliedGridStateRef.current}
      />
      {/* Row-count info stays page-level, outside the universal grid. */}
      {data.rows.length > 0 && (
        <div className="mt-2 px-4 py-2 text-xs text-gray-500">
          {data.rows.length} {data.rows.length === 1 ? 'row' : 'rows'}
        </div>
      )}
    </SpecializedReportShell>
  )
}

export default GeneralLedgerReport
